import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';

export interface Contract {
  id: string;
  status: string;
  docusign_envelope_id?: string;
  docusign_status?: string;
  contract_type?: string;
}

export interface DocuSignStatus {
  status: string;
  envelopeId?: string;
  sentAt?: string;
  completedAt?: string;
  recipients?: any[];
  liveStatus?: string;
}

export interface DocuSignSigningUrl {
  signingUrl: string;
  envelopeId: string;
  recipientId?: string;
}

export interface ContractTemplate {
  id: string;
  service_id: string;
  name: string;
  description?: string;
  contract_type: string;
  status: string;
  created_at: string;
  approved_at?: string;
  expiration_days?: number;
  tenant_name?: string;
  is_active?: boolean;
  fields?: any[];
}

export interface TemplatePreview {
  template_id: string;
  template_name: string;
  preview_text: string;
  sample_order_data: any;
}

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Contract'],
  endpoints: (builder) => ({
    getContracts: builder.query<Contract[], void>({
      query: () => ({ url: '/contracts' }),
      providesTags: ['Contract'],
    }),
    createContract: builder.mutation<Contract, any>({
      query: (body) => ({ url: '/contracts', method: 'POST', body }),
      invalidatesTags: ['Contract'],
    }),
    createFromCatalog: builder.mutation<Contract, { customer_id: string; terms?: string }>({
      query: (body) => ({ url: '/contracts/from-catalog', method: 'POST', body }),
      invalidatesTags: ['Contract'],
    }),
    signContract: builder.mutation<any, { id: string; recipients: any[] }>({
      query: ({ id, recipients }) => ({
        url: `/contracts/${id}/sign`,
        method: 'POST',
        body: { recipients },
      }),
      invalidatesTags: ['Contract'],
    }),
    getMilestones: builder.query<any[], string>({
      query: (id) => ({ url: `/contracts/${id}/milestones` }),
      providesTags: ['Contract'],
    }),
    addMilestone: builder.mutation<any, { contractId: string; body: any }>({
      query: ({ contractId, body }) => ({
        url: `/contracts/${contractId}/milestones`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Contract'],
    }),
    updateMilestone: builder.mutation<any, { contractId: string; milestoneId: string; body: any }>({
      query: ({ contractId, milestoneId, body }) => ({
        url: `/contracts/${contractId}/milestones/${milestoneId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Contract'],
    }),
    // DocuSign endpoints
    getDocuSignStatus: builder.query<DocuSignStatus, string>({
      query: (contractId) => ({ url: `/contracts/${contractId}/docusign/status` }),
      providesTags: ['Contract'],
    }),
    getDocuSignSigningUrl: builder.mutation<DocuSignSigningUrl, string>({
      query: (contractId) => ({
        url: `/contracts/${contractId}/docusign/sign`,
        method: 'POST',
      }),
      invalidatesTags: ['Contract'],
    }),
    resendDocuSignEnvelope: builder.mutation<any, string>({
      query: (contractId) => ({
        url: `/contracts/${contractId}/docusign/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Contract'],
    }),
    // Template management endpoints
    getServiceTemplates: builder.query<ContractTemplate[], string>({
      query: (serviceId) => ({ url: `/contracts/templates/${serviceId}` }),
      providesTags: ['Contract'],
    }),
    getAvailableContractTemplates: builder.query<ContractTemplate[], void>({
      query: () => ({ url: '/contracts/templates/available' }),
      providesTags: ['Contract'],
    }),
    uploadContractTemplate: builder.mutation<{ template_id: string }, FormData>({
      query: (formData) => ({
        url: '/contracts/templates',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Contract'],
    }),
    updateContractTemplate: builder.mutation<any, { id: string; name: string; description: string }>({
      query: ({ id, ...body }) => ({
        url: `/contracts/templates/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Contract'],
    }),
    deleteContractTemplate: builder.mutation<any, string>({
      query: (id) => ({
        url: `/contracts/templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Contract'],
    }),
    getTemplatePreview: builder.query<TemplatePreview, string>({
      query: (id) => ({ url: `/contracts/templates/${id}/preview` }),
      providesTags: ['Contract'],
    }),
    getContractTemplate: builder.query<ContractTemplate, string>({
      query: (id) => ({ url: `/contracts/templates/${id}` }),
      providesTags: ['Contract'],
    }),
    // Admin endpoints
    getAllTemplatesForApproval: builder.query<ContractTemplate[], void>({
      query: () => ({ url: '/admin/contracts/templates' }),
      providesTags: ['Contract'],
    }),
    approveContractTemplate: builder.mutation<any, string>({
      query: (id) => ({
        url: `/admin/contracts/templates/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['Contract'],
    }),
    getContractAnalytics: builder.query<any[], void>({
      query: () => ({ url: '/admin/contracts/analytics' }),
      providesTags: ['Contract'],
    }),
  })
});

export const {
  useGetContractsQuery,
  useCreateContractMutation,
  useCreateFromCatalogMutation,
  useSignContractMutation,
  useGetMilestonesQuery,
  useAddMilestoneMutation,
  useUpdateMilestoneMutation,
  // DocuSign hooks
  useGetDocuSignStatusQuery,
  useGetDocuSignSigningUrlMutation,
  useResendDocuSignEnvelopeMutation,
  // Template management hooks
  useGetServiceTemplatesQuery,
  useGetAvailableContractTemplatesQuery,
  useUploadContractTemplateMutation,
  useUpdateContractTemplateMutation,
  useDeleteContractTemplateMutation,
  useGetTemplatePreviewQuery,
  useGetContractTemplateQuery,
  useGetAllTemplatesForApprovalQuery,
  useApproveContractTemplateMutation,
  useGetContractAnalyticsQuery,
} = contractsApi;
